import { MIQAmenity } from '../redux/actions/system/load-miq-amenities';

/**
 * Normalizes an array of amenities (which can be keys or values) to their corresponding keys.
 * Strategy:
 * 1. Try to match as key first
 * 2. If not found, try to match as value and return its key
 * 3. If not found in MIQ list, discard (filter out)
 * 
 * @param inputArray - Array of amenity keys or values (can be mixed)
 * @param miqAmenities - Array of MIQ amenities with key-value pairs
 * @returns Array of normalized keys
 */
export function normalizeToKeys(inputArray: string[], miqAmenities: MIQAmenity[]): string[] {
    if (!Array.isArray(inputArray) || !Array.isArray(miqAmenities)) {
        return [];
    }

    const normalizedKeys = inputArray
        .map((item: string) => {
            if (!item || typeof item !== 'string') {
                return null;
            }

            const normalizedItem = item.toLowerCase().trim();

            let amenity = miqAmenities.find(a => a && a.key && a.key.toLowerCase().trim() === normalizedItem);
            
            if (amenity) {
                return amenity.key;
            }
            
            amenity = miqAmenities.find(a => a && a.value && a.value.toLowerCase().trim() === normalizedItem);
            
            if (amenity) {
                return amenity.key;
            }
            
            return null;
        })
        .filter((key: string | null) => key !== null) as string[];

    return Array.from(new Set(normalizedKeys));
}

/**
 * Gets the display value for a single amenity key.
 * Returns the key itself if not found in MIQ list.
 * 
 * @param key - Amenity key
 * @param miqAmenities - Array of MIQ amenities with key-value pairs
 * @returns Display value or the original key if not found
 */
export function getDisplayValue(key: string, miqAmenities: MIQAmenity[]): string {
    if (!key || !Array.isArray(miqAmenities)) {
        return key || '';
    }

    const normalizedKey = key.toLowerCase().trim();
    const amenity = miqAmenities.find(a => a && a.key && a.key.toLowerCase().trim() === normalizedKey);
    return amenity && amenity.value ? amenity.value : key;
}

/**
 * Gets available MIQ options for dropdown, excluding already selected items.
 * Returns options in format required by FormSelect component.
 * 
 * @param miqAmenities - Array of MIQ amenities with key-value pairs
 * @param selectedKeys - Array of currently selected amenity keys
 * @returns Array of {label, value} objects for dropdown
 */
export function getAvailableOptions(
    miqAmenities: MIQAmenity[], 
    selectedKeys: string[]
): Array<{ label: string; value: string }> {
    if (!Array.isArray(miqAmenities)) {
        return [];
    }

    // Create case-insensitive lookup set
    const selectedSet = new Set((selectedKeys || []).map(k => k.toLowerCase().trim()));

    return miqAmenities
        .filter(amenity => amenity && amenity.key && !selectedSet.has(amenity.key.toLowerCase().trim()))
        .map(amenity => ({
            label: amenity.value || amenity.key,
            value: amenity.key
        }));
}
